import React, { useEffect, useMemo, useRef } from "react";
import { MdOutlineInfo } from "react-icons/md";
import "./SharedStyles.scss";

interface IProps {
  message: string;
}

const hasOwnStyles = (html: string): boolean =>
  /<style[\s>]/i.test(html) ||
  /<link[^>]+stylesheet/i.test(html) ||
  /<!DOCTYPE/i.test(html);

const FILE_LINK_PATTERN = /\.(pdf|docx?|xlsx?|pptx?|csv|zip|rar|7z|tar|gz|jpg|jpeg|png|gif|svg|webp|mp4|mp3|wav|ogg)(\?|#|$)/i;

const applyLinkSecurity = (root: ParentNode) => {
  root.querySelectorAll("a[href]").forEach((link) => {
    const anchor = link as HTMLAnchorElement;
    const href = anchor.getAttribute("href") || "";

    const isFileLink = FILE_LINK_PATTERN.test(anchor.pathname || href);

    const isInternalSpaLink =
      !isFileLink &&
      (href.startsWith("/") ||
        href.startsWith("#") ||
        href.startsWith("?") ||
        (anchor.hostname && anchor.hostname === window.location.hostname));

    if (isInternalSpaLink) {
      anchor.removeAttribute("target");
      anchor.removeAttribute("rel");
    } else {
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
    }
  });
};

const processHtmlLinks = (html: string): string => {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    applyLinkSecurity(doc.body);
    return doc.body.innerHTML;
  } catch {
    return html;
  }
};

const EAPageMessage: React.FC<IProps> = ({ message }) => {
  const shadowHostRef = useRef<HTMLDivElement>(null);
  const isIsolated = hasOwnStyles(message);
  const processedMessage = useMemo(() => processHtmlLinks(message), [message]);

  useEffect(() => {
    if (!isIsolated) return;
    const host = shadowHostRef.current;
    if (!host) return;

    const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });
    shadow.innerHTML = message;
    applyLinkSecurity(shadow);
  }, [message, isIsolated]);

  if (!message || message.length <= 12) return null;

  return (
    <div className="page-message">
      <MdOutlineInfo className="page-message-icon" />
      {isIsolated ? (
        <div ref={shadowHostRef} />
      ) : (
        <article dangerouslySetInnerHTML={{ __html: processedMessage }} />
      )}
    </div>
  );
};

export default EAPageMessage;
